import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Pages.css';

const emptyForm = { name: '', template_id: '', filterType: 'all', tag: '' };

const PractitionerCampaigns = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [draft, setDraft] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const userRole = user?.role || JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const userId = user?.id || user?.uuid || JSON.parse(localStorage.getItem('user') || '{}')?.uuid;

  const fetchCampaigns = useCallback(async () => {
    const response = await axios.get(`${apiUrl}/practitioners/${userId}/campaigns`, { headers });
    setCampaigns(response.data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, userId, token]);

  useEffect(() => {
    if (userRole !== 'practitioner' && userRole !== 'admin') {
      navigate('/');
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const [campaignsRes, templatesRes, clientsRes] = await Promise.all([
          axios.get(`${apiUrl}/practitioners/${userId}/campaigns`, { headers }),
          axios.get(`${apiUrl}/practitioners/${userId}/email-templates`, { headers }),
          axios.get(`${apiUrl}/practitioners/${userId}/clients`, { headers })
        ]);
        setCampaigns(campaignsRes.data || []);
        setTemplates(templatesRes.data || []);
        setClients(clientsRes.data || []);
      } catch (err) {
        setError('Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, navigate]);

  // While any campaign is actively sending, poll for progress every few seconds.
  useEffect(() => {
    const hasSending = campaigns.some((c) => c.status === 'sending');
    if (!hasSending) return undefined;
    const interval = setInterval(fetchCampaigns, 3000);
    return () => clearInterval(interval);
  }, [campaigns, fetchCampaigns]);

  const allTags = useMemo(() => {
    const tags = new Set();
    clients.forEach((c) => (c.tags || []).forEach((tag) => tags.add(tag)));
    return Array.from(tags).sort();
  }, [clients]);

  const openCreate = () => {
    setFormData(emptyForm);
    setDraft(null);
    setShowForm(true);
  };

  const handleCreateDraft = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const recipient_filter = formData.filterType === 'tag' ? { tag: formData.tag } : { all: true };
      const response = await axios.post(
        `${apiUrl}/practitioners/${userId}/campaigns`,
        { name: formData.name, template_id: formData.template_id, recipient_filter },
        { headers }
      );
      setDraft(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create campaign');
    }
  };

  const handleSend = async () => {
    if (!draft) return;
    setError('');
    try {
      await axios.post(`${apiUrl}/practitioners/${userId}/campaigns/${draft.id}/send`, {}, { headers });
      setShowForm(false);
      setDraft(null);
      fetchCampaigns();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send campaign');
    }
  };

  const openCampaign = async (campaign) => {
    try {
      const response = await axios.get(`${apiUrl}/practitioners/${userId}/campaigns/${campaign.id}`, { headers });
      setSelectedCampaign(response.data);
    } catch (err) {
      setError('Failed to load campaign details');
    }
  };

  if (loading) return <div className="page-container">Loading campaigns...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Email Campaigns</h1>
        <div className="flex-between">
          <button className="btn btn-secondary" onClick={() => navigate('/practitioner/email-templates')}>Manage Templates</button>
          <button className="btn btn-primary" onClick={openCreate} disabled={templates.length === 0}>+ New Campaign</button>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      {templates.length === 0 && (
        <p className="empty-state">Create an email template first, then come back here to send it to your clients.</p>
      )}

      <div className="list-container table-wrap">
        <table className="table">
          <thead>
            <tr><th>Campaign</th><th>Template</th><th>Status</th><th>Progress</th><th></th></tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td><strong>{c.name}</strong></td>
                <td>{c.template_name || <span className="text-muted">—</span>}</td>
                <td><span className={`badge badge-${c.status === 'sent' ? 'confirmed' : c.status}`}>{c.status}</span></td>
                <td>{c.sent_count}/{c.total_count} sent{parseInt(c.failed_count, 10) > 0 ? `, ${c.failed_count} failed` : ''}</td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => openCampaign(c)}>View</button>
                </td>
              </tr>
            ))}
            {campaigns.length === 0 && <tr><td colSpan="5" className="empty-state">No campaigns yet</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Campaign</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>

            {!draft ? (
              <form onSubmit={handleCreateDraft}>
                <div className="form-group">
                  <label>Campaign Name</label>
                  <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Template</label>
                  <select
                    value={formData.template_id}
                    onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                    required
                  >
                    <option value="">Select a template...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Recipients</label>
                  <select
                    value={formData.filterType}
                    onChange={(e) => setFormData({ ...formData, filterType: e.target.value })}
                  >
                    <option value="all">All my clients</option>
                    <option value="tag" disabled={allTags.length === 0}>Clients with a specific tag</option>
                  </select>
                </div>
                {formData.filterType === 'tag' && (
                  <div className="form-group">
                    <label>Tag</label>
                    <select value={formData.tag} onChange={(e) => setFormData({ ...formData, tag: e.target.value })} required>
                      <option value="">Select a tag...</option>
                      {allTags.map((tag) => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </div>
                )}
                <button type="submit" className="btn btn-primary btn-block">Continue</button>
              </form>
            ) : (
              <div>
                <p>
                  This will send <strong>{templates.find((t) => String(t.id) === String(formData.template_id))?.name}</strong> to{' '}
                  <strong>{draft.recipient_count}</strong> client{draft.recipient_count === 1 ? '' : 's'}.
                </p>
                {draft.recipient_count === 0 ? (
                  <p className="empty-state">No matching clients — nothing will be sent.</p>
                ) : (
                  <button className="btn btn-primary btn-block" onClick={handleSend}>Send Now</button>
                )}
                <button className="btn btn-secondary btn-block" style={{ marginTop: 8 }} onClick={() => setDraft(null)}>Back</button>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedCampaign && (
        <div className="modal" onClick={() => setSelectedCampaign(null)}>
          <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedCampaign.name}</h2>
              <button className="close-btn" onClick={() => setSelectedCampaign(null)}>×</button>
            </div>
            {(selectedCampaign.recipients || []).map((r) => (
              <div key={r.id} className="note">
                <div className="note-meta">
                  <span>{r.client_name || r.client_email}</span>
                  <span className={`badge badge-${r.status === 'sent' ? 'confirmed' : r.status === 'failed' ? 'rejected' : 'pending'}`}>
                    {r.status}
                  </span>
                </div>
                {r.error && <div className="note-text text-muted">{r.error}</div>}
              </div>
            ))}
            {(selectedCampaign.recipients || []).length === 0 && <p className="empty-state">No recipients</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default PractitionerCampaigns;
